import { useCallback, useEffect, useRef } from 'react';

interface UseExternalRegistrationCheckOptions {
    /**
     * Функция для проверки статуса регистрации
     * Должна возвращать Promise с результатом проверки
     */
    checkRegistration: () => Promise<boolean | void>;

    /**
     * Вызывается при успешной регистрации
     */
    onRegistrationComplete?: () => void;

    /**
     * Вызывается при ошибке проверки
     */
    onError?: (error: Error) => void;

    /**
     * Включить автоматическую проверку при возврате на вкладку
     * @default true
     */
    enabled?: boolean;

    /**
     * Минимальный интервал между проверками (мс)
     * @default 3000
     */
    checkDelay?: number;
}

export function useExternalRegistrationCheck(options: UseExternalRegistrationCheckOptions) {
    const { checkRegistration, onRegistrationComplete, onError, enabled = true, checkDelay = 3000 } = options;

    const isCheckingRef = useRef(false);
    const isTrackingRef = useRef(false);
    const checkTimeoutRef = useRef<NodeJS.Timeout>();
    const lastCheckTimeRef = useRef<number>(0);

    // Функция проверки регистрации с защитой от дублирования
    const performCheck = useCallback(async () => {
        if (isCheckingRef.current || !enabled || !isTrackingRef.current) {
            return;
        }

        isCheckingRef.current = true;
        lastCheckTimeRef.current = Date.now();

        try {
            const result = await checkRegistration();

            if (result === true) {
                onRegistrationComplete?.();
                // Останавливаем отслеживание после успешной регистрации
                isTrackingRef.current = false;
            }
        } catch (error) {
            onError?.(error instanceof Error ? error : new Error(String(error)));
        } finally {
            isCheckingRef.current = false;
        }
    }, [checkRegistration, onRegistrationComplete, onError, enabled]);

    // Запуск проверки с учетом минимального интервала между проверками
    const scheduleCheck = useCallback(() => {
        if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current);
        }

        const now = Date.now();
        const timeSinceLastCheck = now - lastCheckTimeRef.current;
        const remainingDelay = Math.max(0, checkDelay - timeSinceLastCheck);

        if (remainingDelay === 0) {
            // Прошло достаточно времени - делаем запрос сразу
            performCheck();
        } else {
            // Ждем оставшееся время
            checkTimeoutRef.current = setTimeout(() => {
                performCheck();
            }, remainingDelay);
        }
    }, [performCheck, checkDelay]);

    // Обработчик изменения видимости страницы
    useEffect(() => {
        if (!enabled) {
            return;
        }

        const handleVisibilityChange = () => {
            // Проверяем только если отслеживание активно
            if (!isTrackingRef.current) {
                return;
            }

            if (document.visibilityState === 'visible') {
                // Страница снова видна - запускаем проверку с учетом минимального интервала
                scheduleCheck();
            }
        };

        const handleFocus = () => {
            // Проверяем только если отслеживание активно
            if (!isTrackingRef.current) {
                return;
            }

            // Окно получило фокус - запускаем проверку с учетом минимального интервала
            scheduleCheck();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);

            if (checkTimeoutRef.current) {
                clearTimeout(checkTimeoutRef.current);
            }
        };
    }, [enabled, scheduleCheck]);

    // Функция для открытия окна регистрации
    const openRegistration = useCallback((url: string, target = '_blank') => {
        // Активируем отслеживание
        isTrackingRef.current = true;

        // Открываем окно регистрации
        window.open(url, target);
    }, []);

    // Ручная проверка (если нужно)
    const manualCheck = useCallback(() => {
        performCheck();
    }, [performCheck]);

    // Остановить отслеживание
    const stopTracking = useCallback(() => {
        isTrackingRef.current = false;

        if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current);
        }
    }, []);

    return {
        /**
         * Открыть окно/вкладку регистрации
         */
        openRegistration,

        /**
         * Выполнить проверку вручную
         */
        manualCheck,

        /**
         * Остановить отслеживание
         */
        stopTracking,

        /**
         * Идет ли проверка в данный момент
         */
        isChecking: isCheckingRef.current,

        /**
         * Активно ли отслеживание
         */
        isTracking: isTrackingRef.current,
    };
}
